import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  FormGroup,
  FormArray,
} from "@angular/forms";

export function notEqualValidator(
  controlName1: string,
  controlName2: string
): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    if (!(formGroup instanceof FormGroup)) return null; // ensure it's a FormGroup

    const control1 = formGroup.get(controlName1);
    const control2 = formGroup.get(controlName2);

    if (!control1 || !control2) return null;
    return control1.value === control2.value ? { valuesEqual: true } : null;
  };
}

export function compareValidator(controlName1: string, controlName2: string) {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const group = formGroup as FormGroup;
    const control1 = group.get(controlName1);
    const control2 = group.get(controlName2);
    if (!control1 || !control2) return null;

    const val1 = +control1.value;
    const val2 = +control2.value;

    if (val1 && val2 && val1 >= val2) {
      control1.setErrors({ invalidRange: true });
      control2.setErrors({ invalidRange: true });
      return { invalidRange: true };
    } else {
      // clear previous errors
      if (control1.hasError("invalidRange")) control1.setErrors(null);
      if (control2.hasError("invalidRange")) control2.setErrors(null);
      return null;
    }
  };
}

export function compareSameAxis(...controlNames: string[]): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const group = formGroup as FormGroup;
    const controls = controlNames
      .map((name) => group.get(name))
      .filter((c): c is AbstractControl => !!c);

    if (controls.length < 2) return null;

    const values = controls
      .map((c) => c.value?.toString().trim().toUpperCase())
      .filter((v) => v && v !== "");

    const unique = new Set(values);

    // ✅ Clear any old errors first
    controls.forEach((control) => {
      const errors = control.errors;
      if (errors && errors["sameAxis"]) {
        delete errors["sameAxis"];
        if (Object.keys(errors).length === 0) {
          control.setErrors(null);
        } else {
          control.setErrors(errors);
        }
      }
    });

    // ✅ If duplicates found, mark them
    if (unique.size !== values.length) {
      const duplicates = values.filter((v, i) => values.indexOf(v) !== i);
      controls.forEach((control) => {
        if (
          duplicates.includes(control.value?.toString().trim().toUpperCase())
        ) {
          const existingErrors = control.errors || {};
          control.setErrors({ ...existingErrors, sameAxis: true });
        }
      });
      return { duplicateAxis: true };
    }

    return null;
  };
}

export function checkSamePiezeoSerial(...controlNames: string[]): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const group = formGroup as FormGroup;
    const controls = controlNames
      .map((name) => group.get(name))
      .filter((c): c is AbstractControl => !!c);

    if (controls.length < 2) return null;

    // Filter out empty or null values
    const values = controls
      .map((c) => c.value?.toString().trim().toUpperCase())
      .filter((v) => v && v !== "");

    // If only 0 or 1 serials are filled, no need to check
    if (values.length < 2) return null;

    const uniqueValues = new Set(values);

    // Clear previous sameSerial errors
    controls.forEach((control) => {
      const errors = control.errors;
      if (errors && errors["sameSerial"]) {
        delete errors["sameSerial"];
        control.setErrors(Object.keys(errors).length ? errors : null);
      }
    });

    // Check for duplicates only among non-empty values
    if (uniqueValues.size !== values.length) {
      const duplicates = values.filter((v, i) => values.indexOf(v) !== i);
      controls.forEach((control) => {
        const val = control.value?.toString().trim().toUpperCase();
        if (duplicates.includes(val)) {
          const existingErrors = control.errors || {};
          control.setErrors({ ...existingErrors, sameSerial: true });
        }
      });
      return { sameSerial: true };
    }

    return null;
  };
}

// export function duplicateChannelsValidator(
//   ...controlNames: string[]
// ): ValidatorFn {
//   return (formGroup: AbstractControl): ValidationErrors | null => {
//     const group = formGroup as FormGroup;

//     // Get valid controls by provided names
//     const controls = controlNames
//       .map((name) => group.get(name))
//       .filter((c): c is AbstractControl => !!c);

//     // If less than 2 controls exist, no need to validate
//     if (controls.length < 2) return null;

//     // Extract and normalize values (trim + uppercase)
//     const values = controls
//       .map((c) => c.value?.toString().trim().toUpperCase())
//       .filter((v) => v && v !== "");

//     if (values.length < 2) return null; // nothing to compare

//     // Clear previous 'duplicateChannel' errors
//     controls.forEach((control) => {
//       const errors = control.errors;
//       if (errors && errors["duplicateChannel"]) {
//         delete errors["duplicateChannel"];
//         control.setErrors(Object.keys(errors).length ? errors : null);
//       }
//     });

//     // Detect duplicates
//     const duplicates = values.filter((v, i) => values.indexOf(v) !== i);

//     if (duplicates.length) {
//       // Set 'duplicateChannel' error on each duplicated control
//       controls.forEach((control) => {
//         const val = control.value?.toString().trim().toUpperCase();
//         if (duplicates.includes(val)) {
//           const existingErrors = control.errors || {};
//           control.setErrors({ ...existingErrors, duplicateChannel: true });
//         }
//       });
//       // Return group-level error
//       return { duplicateChannel: true };
//     }

//     return null; // all unique
//   };
// }

type DupOptions = {
  externalControls?: AbstractControl[];               // pass actual controls to mark errors on
  externalValues?: () => (string | number | null | undefined)[]; // or pass a getter for plain values
};

export function duplicateChannelsValidator(
  controlNames: string[],
  options?: DupOptions
): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const group = formGroup as FormGroup;

    // 1) collect controls from the group by name
    const controls = controlNames
      .map((name) => group.get(name))
      .filter((c): c is AbstractControl => !!c);

    if (controls.length < 1 && !options?.externalControls && !options?.externalValues) {
      return null;
    }

    // 2) collect external controls (if any)
    const externalControls = options?.externalControls ?? [];

    // 3) collect all values to compare (normalize: trim + uppercase)
    const normalize = (v: any) =>
      v === null || v === undefined ? null : (v.toString().trim().toUpperCase() || null);

    const groupValues = controls
      .map((c) => normalize(c.value))
      .filter((v): v is string => !!v);

    const extValuesFromControls = externalControls
      .map((c) => normalize(c.value))
      .filter((v): v is string => !!v);

    const extValuesFromGetter = (options?.externalValues?.() ?? [])
      .map((v) => normalize(v))
      .filter((v): v is string => !!v);

    const allExternalValues = [...extValuesFromControls, ...extValuesFromGetter];

    // if nothing to compare, no error
    if (groupValues.length + allExternalValues.length < 2) {
      // clear any previous duplicateChannel errors on target controls
      clearDuplicateErrorOnControls([...controls, ...externalControls]);
      return null;
    }

    // Build full list for duplicate detection
    const allValues = [...groupValues, ...allExternalValues];

    // Detect duplicates (value that appears more than once)
    const duplicates = allValues.filter((v, i) => allValues.indexOf(v) !== i);
    const uniqueDuplicates = Array.from(new Set(duplicates));

    // Clear previous 'duplicateChannel' errors on all involved controls first
    clearDuplicateErrorOnControls([...controls, ...externalControls]);

    if (uniqueDuplicates.length > 0) {
      // Mark errors on group controls that match duplicates
      controls.forEach((control) => {
        const val = normalize(control.value);
        if (val && uniqueDuplicates.includes(val)) {
          const existing = control.errors || {};
          control.setErrors({ ...existing, duplicateChannel: true });
        }
      });

      // Mark errors on external controls if they match duplicates
      externalControls.forEach((control) => {
        const val = normalize(control.value);
        if (val && uniqueDuplicates.includes(val)) {
          const existing = control.errors || {};
          control.setErrors({ ...existing, duplicateChannel: true });
        }
      });

      // Return group-level error as well
      return { duplicateChannel: true };
    }

    return null; // unique
  };
}

// helper to remove duplicateChannel error key cleanly
function clearDuplicateErrorOnControls(controls: AbstractControl[]) {
  controls.forEach((control) => {
    if (!control) return;
    const errors = control.errors;
    if (!errors) return;
    if (errors['duplicateChannel']) {
      delete errors['duplicateChannel'];
      control.setErrors(Object.keys(errors).length ? errors : null);
    }
  });
}


export function compareSameAxisControls(controls: AbstractControl[]): ValidatorFn {
  return (_: AbstractControl) => {
    const values = controls
      .map(c => c.value?.toString().toUpperCase().trim())
      .filter(v => !!v);

    const duplicate = values.filter((v, i) => values.indexOf(v) !== i);

    controls.forEach(c => {
      const v = c.value?.toString().toUpperCase().trim();
      if (duplicate.includes(v)) {
        c.setErrors({ ...(c.errors || {}), sameAxis: true });
      } else {
        if (c.errors) {
          delete c.errors['sameAxis'];
          if (!Object.keys(c.errors).length) c.setErrors(null);
        }
      }
    });

    return duplicate.length ? { duplicateAxis: true } : null;
  };
}


export function duplicateChannelsControlValidator(controls: AbstractControl[]): ValidatorFn {
  return (_: AbstractControl) => {
    const normalize = (v: any) => v?.toString().trim().toUpperCase() || null;

    const values = controls.map(c => normalize(c.value)).filter(v => !!v);

    const duplicate = values.filter((v, i) => values.indexOf(v) !== i);

    controls.forEach(c => {
      const val = normalize(c.value);
      if (duplicate.includes(val)) {
        c.setErrors({ ...(c.errors || {}), duplicateChannel: true });
      } else {
        if (c.errors) {
          delete c.errors['duplicateChannel'];
          if (!Object.keys(c.errors).length) c.setErrors(null);
        }
      }
    });

    return duplicate.length ? { duplicateChannel: true } : null;
  };
}
